import React, { useState, useMemo, useRef, useEffect } from 'react';
import {
  ChevronUpIcon,
  ChevronDownIcon,
  ChevronUpDownIcon,
  InformationCircleIcon,
  EyeIcon,
  MagnifyingGlassIcon,
  FunnelIcon,
} from '@heroicons/react/24/outline';
import PermissionConfigurable from '../PermissionConfigurable';
import PenSquareIcon from '../icons/PenSquareIcon';
import { useDispatchFooter } from '../../contexts/DispatchFooterContext';
import Toggle from '../Toggle';
import userSettingsService from '../../services/userSettings.service';
import { useDisplayPreferences } from '../../contexts/DisplayPreferencesContext';
import { FieldNameBadge } from '../FieldNameBadge';

export interface SelectOption {
  value: string;
  label: string;
}

export interface Column<T> {
  key: string;
  label: string;
  fieldName?: string; // Optional snake_case field identifier for LLM help integration
  sortable?: boolean;
  filterable?: boolean; // Enable column filtering
  editable?: boolean; // Enable inline editing
  type?: 'text' | 'number' | 'date' | 'select'; // Input type for editing
  options?: string[] | SelectOption[]; // For select type - can be simple strings or value/label pairs
  onEnterPress?: (row: T) => void; // Custom handler for Enter key (e.g., open drawer)
  render?: (row: T) => React.ReactNode;
  className?: string;
  responsive?: 'always' | 'sm' | 'md' | 'lg' | 'xl';
  sticky?: boolean; // Make column sticky (fixed during horizontal scroll)
  stickyRight?: boolean; // Make column sticky to the right side
}

export interface FilterPill {
  label: string;
  value: string;
  color: 'green' | 'yellow' | 'red' | 'blue' | 'gray';
}

export interface ToolAction<T> {
  icon: React.ReactNode;
  onClick: (row: T) => void;
  title: string;
  className?: string | ((row: T) => string);
}

export interface ToolsColumnConfig<T> {
  showView?: boolean;
  showEdit?: boolean;
  onView?: (row: T) => void;
  onEdit?: (row: T) => void;
  customActions?: ToolAction<T>[];
}

// Stable empty arrays to prevent re-renders
const EMPTY_ARRAY: any[] = [];

interface DataTableProps<T> {
  title: string;
  icon?: React.ReactNode;
  data: T[];
  columns: Column<T>[];

  // Search
  searchable?: boolean;
  searchPlaceholder?: string;
  onSearch?: (query: string) => (row: T) => boolean;

  // Filter pills
  filterLabel?: string;
  filterPills?: FilterPill[];
  activeFilters?: string[];
  onFilterChange?: (filters: string[]) => void;

  // Pagination
  rowsPerPage?: number;
  showPerPageDropdown?: boolean;
  perPageOptions?: number[];

  // Actions
  onInfoClick?: () => void;
  onViewClick?: () => void;
  customHeaderAction?: React.ReactNode;

  // Column visibility
  showFieldsToggle?: boolean;
  showColumnFilters?: boolean; // Show/hide column filter inputs (default: true)

  // Styling
  className?: string;
  getRowClassName?: (row: T) => string;

  // Data key
  getRowKey: (row: T) => string;

  // Load time
  loadTime?: number | null;
  showLoadTime?: boolean;

  // Loading state
  loading?: boolean;
  /** When set (and not loading), the empty state renders this error instead of
   *  "No data found" — so a failed fetch never masquerades as an empty result
   *  (2026-08-25 sweep; TanTable has the same prop as `errorMessage`). */
  errorMessage?: string;

  // Permissions
  componentId?: string;

  // User settings persistence
  settingsKey?: string; // Unique key for saving column order to user settings

  // Tools Column
  toolsColumn?: boolean;
  toolsConfig?: ToolsColumnConfig<T>;

  // Pagination callbacks
  onSearchChange?: (query: string) => void;
  onPageChange?: (page: number) => void;
  onRowsPerPageChange?: (rowsPerPage: number) => void;
  onSortChange?: (sortColumn: string | null, sortDirection: 'asc' | 'desc') => void;

  // Inline editing
  enableInlineEdit?: boolean;
  onCellEdit?: (rowKey: string, columnKey: string, newValue: any, oldValue: any) => Promise<void> | void;

  // Row interactions
  onRowDoubleClick?: (row: T) => void;
}

function DataTable<T>({
  title,
  icon,
  data,
  columns,
  searchable = true,
  searchPlaceholder = 'Search...',
  onSearch,
  filterLabel,
  filterPills = EMPTY_ARRAY,
  activeFilters = EMPTY_ARRAY,
  onFilterChange,
  rowsPerPage: initialRowsPerPage = 10,
  showPerPageDropdown = false,
  perPageOptions = [10, 25, 50, 100],
  onInfoClick,
  onViewClick,
  customHeaderAction,
  showFieldsToggle = false,
  showColumnFilters = true,
  className = '',
  getRowClassName,
  getRowKey,
  loadTime = null,
  showLoadTime = false,
  loading = false,
  errorMessage,
  componentId,
  settingsKey,
  toolsColumn = false,
  toolsConfig,
  onSearchChange,
  onPageChange,
  onRowsPerPageChange,
  onSortChange,
  enableInlineEdit = false,
  onCellEdit,
  onRowDoubleClick,
}: DataTableProps<T>) {
  // Generate default componentId from title if not provided
  const finalComponentId = componentId || `datatable-${title.toLowerCase().replace(/\s+/g, '-')}`;

  // Footer context for pagination
  const { setPaginationControls } = useDispatchFooter();

  // Field name visibility for LLM help integration
  const { showFieldNames, highlightedFieldId } = useDisplayPreferences();

  const [searchQuery, setSearchQuery] = useState('');
  const [sortColumn, setSortColumn] = useState<string | null>(null);
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(initialRowsPerPage);

  // Inline editing state
  const [editingCell, setEditingCell] = useState<{ rowKey: string; columnKey: string } | null>(null);
  const [editValue, setEditValue] = useState<string>('');
  const [selectedCell, setSelectedCell] = useState<{ rowIndex: number; colIndex: number } | null>(null);
  const [savingCell, setSavingCell] = useState<{ rowKey: string; columnKey: string } | null>(null);
  const editInputRef = useRef<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>(null);
  const tableRef = useRef<HTMLTableElement>(null);
  const tableContainerRef = useRef<HTMLDivElement>(null);

  // Column visibility state
  const [visibleColumns, setVisibleColumns] = useState<Set<string>>(new Set(columns.map(col => col.key)));
  const [showColumnSelector, setShowColumnSelector] = useState(false);

  // Column filter state (popup menu with checkboxes)
  const [columnFilters, setColumnFilters] = useState<Map<string, Set<string>>>(new Map());
  const [activeFilterColumn, setActiveFilterColumn] = useState<string | null>(null);
  const [filterSearchText, setFilterSearchText] = useState<string>('');
  const filterMenuRef = useRef<HTMLDivElement>(null);

  // Filter row state (text-based filtering)
  const [filterRowValues, setFilterRowValues] = useState<Map<string, string>>(new Map());

  // Column order state
  const [columnOrder, setColumnOrder] = useState<string[]>(columns.map(col => col.key));
  const [draggedColumn, setDraggedColumn] = useState<string | null>(null);
  const [columnOrderLoaded, setColumnOrderLoaded] = useState(false);

  // Use stable reference for activeFilters to prevent unnecessary re-renders
  const stableActiveFilters = React.useMemo(() => activeFilters || [], [activeFilters]);
  const stableFilterPills = React.useMemo(() => filterPills || [], [filterPills]);

  // Load column order from user settings on mount
  useEffect(() => {
    const loadColumnOrder = async () => {
      if (!settingsKey || columnOrderLoaded) return;

      try {
        const setting = await userSettingsService.getUserSetting(`${settingsKey}_column_order`);
        if (setting && setting.setting_value) {
          // setting_value is already parsed as an object/array by the backend
          const savedOrder = Array.isArray(setting.setting_value)
            ? setting.setting_value
            : (typeof setting.setting_value === 'string' ? JSON.parse(setting.setting_value) : []);

          // Validate that saved order contains valid column keys
          const validKeys = columns.map(col => col.key);
          const validOrder = savedOrder.filter((key: string) => validKeys.includes(key));

          // Add any new columns that weren't in saved order
          const missingKeys = validKeys.filter(key => !validOrder.includes(key));
          const finalOrder = [...validOrder, ...missingKeys];

          setColumnOrder(finalOrder);
          console.log(`Loaded column order for ${settingsKey}:`, finalOrder);
        }
      } catch (error) {
        console.error('Error loading column order:', error);
      } finally {
        setColumnOrderLoaded(true);
      }
    };

    loadColumnOrder();
  }, [settingsKey, columns, columnOrderLoaded]);

  // Save column order to user settings when it changes
  useEffect(() => {
    const saveColumnOrder = async () => {
      if (!settingsKey || !columnOrderLoaded) return;

      try {
        await userSettingsService.saveUserSetting(
          `${settingsKey}_column_order`,
          columnOrder
        );
        console.log(`Saved column order for ${settingsKey}:`, columnOrder);
      } catch (error) {
        console.error('Error saving column order:', error);
      }
    };

    saveColumnOrder();
  }, [columnOrder, settingsKey, columnOrderLoaded]);

  // Close filter menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (filterMenuRef.current && !filterMenuRef.current.contains(event.target as Node)) {
        setActiveFilterColumn(null);
        setFilterSearchText('');
      }
    };

    if (activeFilterColumn) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [activeFilterColumn]);

  // Focus input when editing starts
  useEffect(() => {
    if (editingCell && editInputRef.current) {
      editInputRef.current.focus();
      if ('select' in editInputRef.current && typeof editInputRef.current.select === 'function') {
        editInputRef.current.select();
      }
    }
  }, [editingCell]);

  // Scroll selected cell into view
  useEffect(() => {
    if (selectedCell && tableRef.current && tableContainerRef.current) {
      const { rowIndex, colIndex } = selectedCell;
      const tbody = tableRef.current.querySelector('tbody');
      if (!tbody) return;

      const rows = tbody.querySelectorAll('tr');
      const targetRow = rows[rowIndex];
      if (!targetRow) return;

      const cells = targetRow.querySelectorAll('td');
      const targetCell = cells[colIndex]; // No offset needed - tools column is now at the end
      if (!targetCell) return;

      // Scroll cell into view (both horizontally and vertically)
      targetCell.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
        inline: 'nearest'
      });
    }
  }, [selectedCell]);

  // Initialize visible columns and column order when columns change
  React.useEffect(() => {
    setVisibleColumns(new Set(columns.map(col => col.key)));
    setColumnOrder(columns.map(col => col.key));
  }, [columns]);

  // Column visibility functions
  const toggleColumn = (columnKey: string) => {
    setVisibleColumns(prev => {
      const newSet = new Set(prev);
      if (newSet.has(columnKey)) {
        newSet.delete(columnKey);
      } else {
        newSet.add(columnKey);
      }
      return newSet;
    });
  };

  const toggleAllColumns = () => {
    if (visibleColumns.size === columns.length) {
      setVisibleColumns(new Set());
    } else {
      setVisibleColumns(new Set(columns.map(col => col.key)));
    }
  };

  // Column filter functions (for popup menu)
  const getUniqueValuesForColumn = (columnKey: string) => {
    const values = new Set<string>();
    data.forEach(row => {
      const value = (row as any)[columnKey];
      if (value !== null && value !== undefined && value !== '') {
        values.add(String(value));
      }
    });
    return Array.from(values).sort();
  };

  const toggleFilterValue = (columnKey: string, value: string) => {
    setColumnFilters(prev => {
      const newFilters = new Map(prev);
      const existingFilter = newFilters.get(columnKey);

      if (!existingFilter) {
        newFilters.set(columnKey, new Set([value]));
      } else {
        const newValues = new Set(existingFilter);
        if (newValues.has(value)) {
          newValues.delete(value);
        } else {
          newValues.add(value);
        }

        if (newValues.size === 0) {
          newFilters.delete(columnKey);
        } else {
          newFilters.set(columnKey, newValues);
        }
      }

      return newFilters;
    });
  };

  const clearColumnFilter = (columnKey: string) => {
    setColumnFilters(prev => {
      const newFilters = new Map(prev);
      newFilters.delete(columnKey);
      return newFilters;
    });
  };

  const clearAllColumnFilters = () => {
    setColumnFilters(new Map());
  };

  // Filter row handlers
  const handleFilterRowChange = (columnKey: string, value: string) => {
    setFilterRowValues(prev => {
      const newFilters = new Map(prev);
      if (value === '') {
        newFilters.delete(columnKey);
      } else {
        newFilters.set(columnKey, value);
      }
      return newFilters;
    });
  };

  // Inline editing handlers
  const handleCellClick = (rowIndex: number, colIndex: number) => {
    if (!enableInlineEdit) return;
    setSelectedCell({ rowIndex, colIndex });
  };

  const startEditing = (rowIndex: number, colIndex: number) => {
    const row = paginatedData[rowIndex];
    const column = visibleColumnsArray[colIndex];

    if (!row || !column || column.editable === false) return;

    const rowKey = getRowKey(row);
    const columnKey = column.key;
    const value = (row as any)[columnKey];

    // Format value based on column type
    let formattedValue = '';
    if (value) {
      if (column.type === 'date') {
        // Skip invalid dates like "0000-00-00"
        if (value === '0000-00-00' || value.toString().startsWith('0000')) {
          formattedValue = '';
        } else {
          // Convert to YYYY-MM-DD format for date input
          const dateObj = value instanceof Date ? value : new Date(value);
          if (!isNaN(dateObj.getTime())) {
            formattedValue = dateObj.toISOString().split('T')[0];
          }
        }
      } else {
        formattedValue = value.toString();
      }
    }

    setEditingCell({ rowKey, columnKey });
    setEditValue(formattedValue);
  };

  const handleCellDoubleClick = (rowIndex: number, colIndex: number) => {
    startEditing(rowIndex, colIndex);
  };

  const commitEdit = async () => {
    if (!editingCell) return;

    const row = paginatedData.find(r => getRowKey(r) === editingCell.rowKey);
    if (!row) return;

    const column = columns.find(c => c.key === editingCell.columnKey);
    if (!column) return;

    const oldValue = (row as any)[editingCell.columnKey];
    let parsedValue: any = editValue;

    // Type conversion
    if (column.type === 'number') {
      parsedValue = editValue ? parseFloat(editValue) : null;
    } else if (column.type === 'date') {
      parsedValue = editValue ? new Date(editValue) : null;
    }

    // Clear editing state
    setEditingCell(null);
    setEditValue('');

    // Only save if value changed
    if (parsedValue !== oldValue) {
      // Show green background while saving
      setSavingCell({ rowKey: editingCell.rowKey, columnKey: editingCell.columnKey });

      try {
        if (onCellEdit) {
          await onCellEdit(editingCell.rowKey, editingCell.columnKey, parsedValue, oldValue);
        }

        // Keep green for a moment to show success
        setTimeout(() => {
          setSavingCell(null);
        }, 500);
      } catch (error) {
        // Clear green on error
        setSavingCell(null);
        throw error;
      }
    }

    // Refocus table for keyboard navigation
    setTimeout(() => {
      if (tableContainerRef.current) {
        tableContainerRef.current.focus();
      }
    }, 0);
  };

  const handleEditKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      commitEdit();
    } else if (e.key === 'Escape') {
      setEditingCell(null);
      setEditValue('');
      // Refocus table for keyboard navigation
      setTimeout(() => tableContainerRef.current?.focus(), 0);
    } else if (e.key === 'Tab') {
      e.preventDefault();
      commitEdit();
      // Navigate after commit
      if (selectedCell) {
        const { rowIndex, colIndex } = selectedCell;
        // Tab moves to next column, wraps to next row
        if (colIndex < visibleColumnsArray.length - 1) {
          setSelectedCell({ rowIndex, colIndex: colIndex + 1 });
        } else if (rowIndex < paginatedData.length - 1) {
          setSelectedCell({ rowIndex: rowIndex + 1, colIndex: 0 });
        }
      }
    }
  };

  // Keyboard navigation for table
  const handleTableKeyDown = (e: React.KeyboardEvent) => {
    if (editingCell) return; // Don't navigate while editing

    if (!selectedCell) {
      // Start at first cell
      setSelectedCell({ rowIndex: 0, colIndex: 0 });
      return;
    }

    const { rowIndex, colIndex } = selectedCell;

    switch (e.key) {
      case 'ArrowUp':
        e.preventDefault();
        if (rowIndex > 0) {
          setSelectedCell({ rowIndex: rowIndex - 1, colIndex });
        }
        break;
      case 'ArrowDown':
        e.preventDefault();
        if (rowIndex < paginatedData.length - 1) {
          setSelectedCell({ rowIndex: rowIndex + 1, colIndex });
        }
        break;
      case 'ArrowLeft':
        e.preventDefault();
        if (colIndex > 0) {
          setSelectedCell({ rowIndex, colIndex: colIndex - 1 });
        }
        break;
      case 'ArrowRight':
        e.preventDefault();
        if (colIndex < visibleColumnsArray.length - 1) {
          setSelectedCell({ rowIndex, colIndex: colIndex + 1 });
        }
        break;
      case 'Enter':
        e.preventDefault();
        // Check if column has custom Enter handler (e.g., for opening drawer)
        const column = visibleColumnsArray[colIndex];
        const row = paginatedData[rowIndex];
        if (column?.onEnterPress && row) {
          column.onEnterPress(row);
        } else {
          startEditing(rowIndex, colIndex);
        }
        break;
      case 'Tab':
        e.preventDefault();
        // Tab moves to next column, wraps to next row
        if (colIndex < visibleColumnsArray.length - 1) {
          setSelectedCell({ rowIndex, colIndex: colIndex + 1 });
        } else if (rowIndex < paginatedData.length - 1) {
          setSelectedCell({ rowIndex: rowIndex + 1, colIndex: 0 });
        }
        break;
    }
  };

  // Drag and drop handlers
  const handleDragStart = (columnKey: string) => {
    setDraggedColumn(columnKey);
  };

  const handleDragOver = (e: React.DragEvent, targetColumnKey: string) => {
    e.preventDefault();
    if (!draggedColumn || draggedColumn === targetColumnKey) return;

    const newOrder = [...columnOrder];
    const draggedIdx = newOrder.indexOf(draggedColumn);
    const targetIdx = newOrder.indexOf(targetColumnKey);

    newOrder.splice(draggedIdx, 1);
    newOrder.splice(targetIdx, 0, draggedColumn);

    setColumnOrder(newOrder);
  };

  const handleDragEnd = () => {
    setDraggedColumn(null);
  };

  // Filter and order columns based on visibility and user-defined order
  const visibleColumnsArray = useMemo(() => {
    const columnsMap = new Map(columns.map(col => [col.key, col]));
    return columnOrder
      .map(key => columnsMap.get(key))
      .filter((col): col is Column<T> => col !== undefined && visibleColumns.has(col.key));
  }, [columns, columnOrder, visibleColumns]);


  // Filter and sort data
  const filteredData = useMemo(() => {
    let result = [...data];

    // Apply column filters (popup menu with checkboxes)
    if (columnFilters.size > 0) {
      result = result.filter(row => {
        for (const [columnKey, filterValues] of Array.from(columnFilters.entries())) {
          const cellValue = String((row as any)[columnKey] ?? '');
          if (filterValues.size > 0 && !filterValues.has(cellValue)) {
            return false;
          }
        }
        return true;
      });
    }

    // Apply filter row (text-based filtering)
    if (filterRowValues.size > 0) {
      result = result.filter(row => {
        for (const [columnKey, filterText] of Array.from(filterRowValues.entries())) {
          const cellValue = String((row as any)[columnKey] ?? '').toLowerCase();
          if (!cellValue.includes(filterText.toLowerCase())) {
            return false;
          }
        }
        return true;
      });
    }

    // Apply search filter
    if (searchQuery && onSearch) {
      const filterFn = onSearch(searchQuery);
      result = result.filter(filterFn);
    }

    // Apply sorting
    if (sortColumn) {
      result.sort((a: any, b: any) => {
        const aVal = a[sortColumn] || '';
        const bVal = b[sortColumn] || '';

        if (typeof aVal === 'string' && typeof bVal === 'string') {
          return sortDirection === 'asc'
            ? aVal.localeCompare(bVal)
            : bVal.localeCompare(aVal);
        }

        return sortDirection === 'asc'
          ? aVal > bVal ? 1 : -1
          : bVal > aVal ? 1 : -1;
      });
    }

    return result;
  }, [data, columnFilters, filterRowValues, searchQuery, sortColumn, sortDirection, onSearch]);

  // Paginate data
  const paginatedData = useMemo(() => {
    const startIndex = (currentPage - 1) * rowsPerPage;
    return filteredData.slice(startIndex, startIndex + rowsPerPage);
  }, [filteredData, currentPage, rowsPerPage]);

  const totalPages = Math.ceil(filteredData.length / rowsPerPage);

  // Adjust current page if it exceeds total pages
  React.useEffect(() => {
    if (currentPage > totalPages && totalPages > 0) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  const handleSort = (columnKey: string) => {
    if (sortColumn === columnKey) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(columnKey);
      setSortDirection('asc');
    }
  };

  const handleFilterToggle = (value: string) => {
    if (!onFilterChange) return;

    if (stableActiveFilters.includes(value)) {
      onFilterChange(stableActiveFilters.filter((f) => f !== value));
    } else {
      onFilterChange([...stableActiveFilters, value]);
    }
  };

  const getFilterColor = (color: string, active: boolean) => {
    if (active) {
      switch (color) {
        case 'green': return 'bg-green-600 text-white hover:bg-green-700';
        case 'yellow': return 'bg-yellow-600 text-white hover:bg-yellow-700';
        case 'red': return 'bg-red-600 text-white hover:bg-red-700';
        case 'blue': return 'bg-blue-600 text-white hover:bg-blue-700';
        case 'gray': return 'bg-gray-600 text-white hover:bg-gray-700';
        default: return 'bg-gray-600 text-white hover:bg-gray-700';
      }
    }
    // Inactive state with color-coded borders
    switch (color) {
      case 'green': return 'bg-panel text-green-700 dark:text-green-300 border-2 border-green-300 dark:border-green-700 hover:bg-green-50 dark:hover:bg-green-900/20';
      case 'yellow': return 'bg-panel text-yellow-700 dark:text-yellow-300 border-2 border-yellow-300 dark:border-yellow-700 hover:bg-yellow-50 dark:hover:bg-yellow-900/20';
      case 'red': return 'bg-panel text-red-700 dark:text-red-300 border-2 border-red-300 dark:border-red-700 hover:bg-red-50 dark:hover:bg-red-900/20';
      case 'blue': return 'bg-panel text-blue-700 dark:text-blue-300 border-2 border-blue-300 dark:border-blue-700 hover:bg-blue-50 dark:hover:bg-blue-900/20';
      case 'gray': return 'bg-panel text-label border-2 border-gray-300 dark:border-gray-600 hover:bg-highlight';
      default: return 'bg-panel text-label border-2 border-gray-300 dark:border-gray-600 hover:bg-highlight';
    }
  };

  // Reset to page 1 when search or filters change
  React.useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, stableActiveFilters, columnFilters, filterRowValues]);

  // Call callbacks when state changes
  React.useEffect(() => {
    onSearchChange?.(searchQuery);
  }, [searchQuery, onSearchChange]);

  React.useEffect(() => {
    onPageChange?.(currentPage);
  }, [currentPage, onPageChange]);

  React.useEffect(() => {
    onRowsPerPageChange?.(rowsPerPage);
  }, [rowsPerPage, onRowsPerPageChange]);

  React.useEffect(() => {
    onSortChange?.(sortColumn, sortDirection);
  }, [sortColumn, sortDirection, onSortChange]);

  // Push pagination controls to footer
  useEffect(() => {
    if (totalPages > 1) {
      const maxVisiblePages = 5;
      let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
      let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

      if (endPage - startPage < maxVisiblePages - 1) {
        startPage = Math.max(1, endPage - maxVisiblePages + 1);
      }

      const pageNumbers = [];
      for (let i = startPage; i <= endPage; i++) {
        pageNumbers.push(i);
      }

      const paginationJSX = (
        <div className="flex flex-1 items-center w-full">
          {/* Mobile: Simple Previous/Next */}
          <div className="flex flex-1 justify-between sm:hidden">
            <button
              onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
              disabled={currentPage === 1}
              className="relative inline-flex items-center rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50"
            >
              Previous
            </button>
            <button
              onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
              disabled={currentPage === totalPages}
              className="relative ml-3 inline-flex items-center rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50"
            >
              Next
            </button>
          </div>

          {/* Desktop: Full pagination */}
          <div className="hidden sm:flex sm:flex-1 sm:items-center sm:gap-4">
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-300">
                Showing{' '}
                <span className="font-medium">{Math.min((currentPage - 1) * rowsPerPage + 1, filteredData.length)}</span>
                {' '}to{' '}
                <span className="font-medium">{Math.min(currentPage * rowsPerPage, filteredData.length)}</span>
                {' '}of{' '}
                <span className="font-medium">{filteredData.length}</span>
                {' '}results
              </p>
            </div>
            <div className="ml-auto">
              <nav aria-label="Pagination" className="isolate inline-flex -space-x-px rounded-md">
                {/* Previous arrow */}
                <button
                  onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                  disabled={currentPage === 1}
                  className="relative inline-flex items-center rounded-l-md px-2 py-2 text-gray-600 dark:text-gray-400 ring-1 ring-inset ring-gray-300 dark:ring-gray-600 hover:bg-highlight focus:z-20 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <span className="sr-only">Previous</span>
                  <svg viewBox="0 0 20 20" fill="currentColor" aria-hidden="true" className="size-5">
                    <path d="M11.78 5.22a.75.75 0 0 1 0 1.06L8.06 10l3.72 3.72a.75.75 0 1 1-1.06 1.06l-4.25-4.25a.75.75 0 0 1 0-1.06l4.25-4.25a.75.75 0 0 1 1.06 0Z" clipRule="evenodd" fillRule="evenodd" />
                  </svg>
                </button>

                {/* First page if not in range */}
                {startPage > 1 && (
                  <>
                    <button
                      onClick={() => setCurrentPage(1)}
                      className="relative inline-flex items-center px-4 py-2 text-sm font-semibold text-gray-700 dark:text-gray-200 ring-1 ring-inset ring-gray-300 dark:ring-gray-600 hover:bg-highlight focus:z-20"
                    >
                      1
                    </button>
                    {startPage > 2 && (
                      <span className="relative inline-flex items-center px-4 py-2 text-sm font-semibold text-muted ring-1 ring-inset ring-gray-300 dark:ring-gray-600">
                        ...
                      </span>
                    )}
                  </>
                )}

                {/* Page numbers */}
                {pageNumbers.map(number => (
                  <button
                    key={number}
                    onClick={() => setCurrentPage(number)}
                    className={`relative inline-flex items-center px-4 py-2 text-sm font-semibold focus:z-20 ${
                      currentPage === number
                        ? 'z-10 bg-blue-600 text-white focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600'
                        : 'text-gray-700 dark:text-gray-200 ring-1 ring-inset ring-gray-300 dark:ring-gray-600 hover:bg-highlight'
                    }`}
                  >
                    {number}
                  </button>
                ))}

                {/* Last page if not in range */}
                {endPage < totalPages && (
                  <>
                    {endPage < totalPages - 1 && (
                      <span className="relative inline-flex items-center px-4 py-2 text-sm font-semibold text-muted ring-1 ring-inset ring-gray-300 dark:ring-gray-600">
                        ...
                      </span>
                    )}
                    <button
                      onClick={() => setCurrentPage(totalPages)}
                      className="relative inline-flex items-center px-4 py-2 text-sm font-semibold text-gray-700 dark:text-gray-200 ring-1 ring-inset ring-gray-300 dark:ring-gray-600 hover:bg-highlight focus:z-20"
                    >
                      {totalPages}
                    </button>
                  </>
                )}

                {/* Next arrow */}
                <button
                  onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                  disabled={currentPage === totalPages}
                  className="relative inline-flex items-center rounded-r-md px-2 py-2 text-gray-600 dark:text-gray-400 ring-1 ring-inset ring-gray-300 dark:ring-gray-600 hover:bg-highlight focus:z-20 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <span className="sr-only">Next</span>
                  <svg viewBox="0 0 20 20" fill="currentColor" aria-hidden="true" className="size-5">
                    <path d="M8.22 5.22a.75.75 0 0 1 1.06 0l4.25 4.25a.75.75 0 0 1 0 1.06l-4.25 4.25a.75.75 0 0 1-1.06-1.06L11.94 10 8.22 6.28a.75.75 0 0 1 0-1.06Z" clipRule="evenodd" fillRule="evenodd" />
                  </svg>
                </button>
              </nav>
            </div>
          </div>
        </div>
      );

      setPaginationControls(paginationJSX);
    } else {
      setPaginationControls(null);
    }

    // Cleanup on unmount
    return () => {
      setPaginationControls(null);
    };
  }, [currentPage, totalPages, rowsPerPage, filteredData.length, showPerPageDropdown, perPageOptions, setPaginationControls]);

  const tableContent = (
    <div className={`bg-white dark:bg-gray-800 rounded-lg shadow flex flex-col h-full ${className || 'mx-4 sm:mx-6'}`} style={{ paddingBottom: '64px' }}>
      {/* Header */}
      <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex-shrink-0">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-semibold text-heading flex items-center gap-2">
            {icon}
            {title} {loading ? (
              <span className="text-muted animate-pulse">Loading<span className="inline-block w-12">...</span></span>
            ) : (
              <>({filteredData.length} {filteredData.length === 1 ? 'item' : 'items'})</>
            )}
          </h2>
          <div className="flex items-center gap-2">
            {showLoadTime && loadTime !== null && (
              <div className="text-xs text-gray-400 mr-2">
                {(loadTime / 1000).toFixed(2)}s
              </div>
            )}
            {customHeaderAction}
            {showFieldsToggle && data.length > 0 && (
              <button
                onClick={() => setShowColumnSelector(!showColumnSelector)}
                className="px-4 py-2 text-label hover:text-gray-900 dark:hover:text-white transition-colors flex items-center gap-2"
              >
                <span className="font-medium">Fields</span>
                <span className="text-xs text-muted">
                  ({visibleColumns.size}/{columns.length})
                </span>
              </button>
            )}
            {onInfoClick && (
              <button
                onClick={onInfoClick}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
              >
                <InformationCircleIcon className="w-6 h-6" />
              </button>
            )}
            {onViewClick && (
              <button
                onClick={onViewClick}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
              >
                <EyeIcon className="w-6 h-6" />
              </button>
            )}
          </div>
        </div>

        {/* Search and Per Page */}
        {searchable && (
          <div className="flex items-center gap-4 mb-4">
            <div className="flex-1">
              <input
                type="text"
                placeholder={searchPlaceholder}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
              />
            </div>
            {showPerPageDropdown && (
              <div className="flex items-center gap-2">
                <label htmlFor="perPage" className="text-sm text-label whitespace-nowrap">
                  Per Page:
                </label>
                <select
                  id="perPage"
                  value={rowsPerPage}
                  onChange={(e) => {
                    setRowsPerPage(Number(e.target.value));
                    setCurrentPage(1); // Reset to first page when changing rows per page
                  }}
                  className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white text-sm"
                >
                  {perPageOptions.map((option) => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </select>
              </div>
            )}
          </div>
        )}

        {/* Filter Pills */}
        {stableFilterPills.length > 0 && (
          <div className="mb-4 flex-shrink-0">
            <div className="flex items-center justify-between mb-2">
              {filterLabel && (
                <span className="text-sm font-medium text-label">{filterLabel}</span>
              )}
              {stableActiveFilters.length > 0 && (
                <button
                  onClick={() => onFilterChange?.([])}
                  className="text-sm text-blue-600 dark:text-blue-400 hover:underline"
                >
                  Clear All
                </button>
              )}
            </div>
            <div className="flex flex-wrap gap-2">
              {stableFilterPills.map((pill) => (
                <button
                  key={pill.value}
                  onClick={() => handleFilterToggle(pill.value)}
                  className={`px-2.5 py-1 rounded-lg text-xs font-medium transition-all ${getFilterColor(
                    pill.color,
                    stableActiveFilters.includes(pill.value)
                  )} ${stableActiveFilters.includes(pill.value) ? 'ring-2 ring-offset-2 ring-blue-500 dark:ring-offset-gray-800' : ''}`}
                >
                  {pill.label}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Column Selector Panel */}
      {showFieldsToggle && showColumnSelector && data.length > 0 && (
        <div className="px-4 sm:px-6 pb-4 border-b border-gray-200 dark:border-gray-700 flex-shrink-0">
          <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-semibold text-heading">
                Column Visibility ({visibleColumns.size} / {columns.length})
              </h3>
              <button
                onClick={toggleAllColumns}
                className="text-sm text-blue-600 dark:text-blue-400 hover:underline"
              >
                {visibleColumns.size === columns.length ? 'Hide All' : 'Show All'}
              </button>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
              {[...columns].sort((a, b) => a.label.localeCompare(b.label)).map(col => (
                <div
                  key={col.key}
                  className="flex items-center gap-2 p-2 rounded hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                >
                  <Toggle
                    size="sm"
                    checked={visibleColumns.has(col.key)}
                    onChange={() => toggleColumn(col.key)}
                  />
                  <span className="text-sm text-label truncate cursor-pointer" title={col.label} onClick={() => toggleColumn(col.key)}>
                    {col.label}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Table - force min-width to ensure horizontal scrollbar appears */}
      <div
        ref={tableContainerRef}
        className="flex-1 overflow-x-scroll overflow-y-auto outline-none"
        tabIndex={0}
        onKeyDown={handleTableKeyDown}
      >
        <table ref={tableRef} className="divide-y divide-gray-200 dark:divide-gray-700" style={{ minWidth: 'max-content' }}>
          <thead className="sticky top-0 bg-gray-50 dark:bg-gray-700 z-20">
            <tr>
              {visibleColumnsArray.map((column, index) => {
                // Only apply responsive classes if showFieldsToggle is disabled
                // When showFieldsToggle is enabled, user controls visibility manually
                const responsiveClass = !showFieldsToggle && column.responsive
                  ? column.responsive === 'always'
                    ? ''
                    : `hidden ${column.responsive}:table-cell`
                  : '';

                return (
                  <th
                    key={column.key}
                    draggable
                    onDragStart={() => handleDragStart(column.key)}
                    onDragOver={(e) => handleDragOver(e, column.key)}
                    onDragEnd={handleDragEnd}
                    className={`px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider whitespace-nowrap ${
                      draggedColumn === column.key ? 'opacity-50' : ''
                    } ${column.sortable ? 'cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600' : ''} ${responsiveClass} ${column.className || ''} ${
                      column.stickyRight
                        ? 'sticky right-0 top-0 bg-gray-50 dark:bg-gray-700 z-40 shadow-[-2px_0_5px_-2px_rgba(0,0,0,0.1)]'
                        : column.sticky
                          ? 'sticky left-0 top-0 bg-gray-50 dark:bg-gray-700 z-40 shadow-[2px_0_5px_-2px_rgba(0,0,0,0.1)]'
                          : 'bg-gray-50 dark:bg-gray-700'
                    }`}
                    style={{
                      cursor: draggedColumn === column.key ? 'grabbing' : 'grab'
                    }}
                  >
                    <div className="flex items-center gap-1" data-field-name={column.fieldName || undefined}>
                      <div
                        onClick={() => column.sortable && handleSort(column.key)}
                        className="flex items-center gap-1 flex-1"
                      >
                        {column.label}
                        {column.sortable && (
                          sortColumn === column.key ? (
                            sortDirection === 'asc' ? (
                              <ChevronUpIcon className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                            ) : (
                              <ChevronDownIcon className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                            )
                          ) : (
                            <ChevronUpDownIcon className="w-4 h-4 text-gray-400" />
                          )
                        )}
                      </div>
                      {showFieldNames && column.fieldName && (
                        <FieldNameBadge
                          fieldName={column.fieldName}
                          isHighlighted={highlightedFieldId === column.fieldName}
                        />
                      )}
                    </div>
                  </th>
                );
              })}
              {/* Tools Column Header */}
              {toolsColumn && (
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider w-24 sticky right-0 top-0 bg-gray-50 dark:bg-gray-700 z-40 shadow-[-2px_0_5px_-2px_rgba(0,0,0,0.1)]">
                  Tools
                </th>
              )}
            </tr>
            {/* Filter Row */}
            <tr className="bg-gray-100 dark:bg-gray-600">
              {visibleColumnsArray.map((column) => {
                const responsiveClass = !showFieldsToggle && column.responsive
                  ? column.responsive === 'always'
                    ? ''
                    : `hidden ${column.responsive}:table-cell`
                  : '';

                const filterValue = filterRowValues.get(column.key) || '';

                return (
                  <th
                    key={column.key}
                    className={`px-2 py-2 relative ${responsiveClass} ${column.className || ''} ${
                      column.stickyRight
                        ? 'sticky right-0 bg-gray-100 dark:bg-gray-600 z-40 shadow-[-2px_0_5px_-2px_rgba(0,0,0,0.1)]'
                        : column.sticky
                          ? 'sticky left-0 bg-gray-100 dark:bg-gray-600 z-40 shadow-[2px_0_5px_-2px_rgba(0,0,0,0.1)]'
                          : 'bg-gray-100 dark:bg-gray-600'
                    }`}
                  >
                    {showColumnFilters && column.filterable && column.key !== 'actions' ? (
                      (() => {
                        const hasFilter = columnFilters.has(column.key);
                        const isFilterMenuOpen = activeFilterColumn === column.key;
                        const uniqueValues = isFilterMenuOpen ? getUniqueValuesForColumn(column.key) : [];
                        const activeFilter = columnFilters.get(column.key);

                        return (
                          <>
                            <input
                              type="text"
                              value={filterValue}
                              onChange={(e) => handleFilterRowChange(column.key, e.target.value)}
                              onClick={() => {
                                setActiveFilterColumn(isFilterMenuOpen ? null : column.key);
                              }}
                              placeholder={`Filter ${column.label}...`}
                              className="w-full px-2 py-1 text-xs border border-gray-300 dark:border-gray-500 rounded bg-white dark:bg-gray-700 text-heading placeholder-gray-400 dark:placeholder-gray-500 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            />

                            {/* Filter Dropdown */}
                            {isFilterMenuOpen && (
                              <div
                                ref={filterMenuRef}
                                className="absolute left-0 top-full mt-1 w-64 bg-panel border border-gray-300 dark:border-gray-600 rounded-lg shadow-lg z-50 max-h-96 overflow-y-auto"
                                onClick={(e) => e.stopPropagation()}
                              >
                                <div className="p-2">
                                  <div className="flex items-center justify-between mb-2 px-2">
                                    <span className="text-xs font-semibold text-muted">
                                      Filter by {column.label}
                                    </span>
                                    {hasFilter && (
                                      <button
                                        onClick={() => clearColumnFilter(column.key)}
                                        className="text-xs text-red-600 dark:text-red-400 hover:underline"
                                      >
                                        Clear
                                      </button>
                                    )}
                                  </div>

                                  {/* Values List */}
                                  <div className="space-y-1 max-h-64 overflow-y-auto">
                                    {uniqueValues
                                      .filter(value => {
                                        if (filterValue === '') return true;

                                        // For select columns, search by both value and label
                                        if (column.type === 'select' && column.options && Array.isArray(column.options)) {
                                          const options = column.options as (string | SelectOption)[];
                                          const option = options.find((opt: string | SelectOption) => {
                                            if (typeof opt === 'string') {
                                              return opt === value;
                                            }
                                            return opt.value === value;
                                          });
                                          if (option && typeof option !== 'string') {
                                            const label = option.label;
                                            return label.toLowerCase().includes(filterValue.toLowerCase()) ||
                                                   value.toLowerCase().includes(filterValue.toLowerCase());
                                          }
                                        }

                                        return value.toLowerCase().includes(filterValue.toLowerCase());
                                      })
                                      .map(value => {
                                        const isSelected = activeFilter?.has(value) || false;

                                        // If column has select options, display the label instead of value
                                        let displayValue = value;
                                        if (column.type === 'select' && column.options && Array.isArray(column.options)) {
                                          const options = column.options as (string | SelectOption)[];
                                          const option = options.find((opt: string | SelectOption) => {
                                            if (typeof opt === 'string') {
                                              return opt === value;
                                            }
                                            return opt.value === value;
                                          });
                                          if (option && typeof option !== 'string') {
                                            displayValue = option.label;
                                          }
                                        }

                                        return (
                                          <label
                                            key={value}
                                            className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-gray-100 dark:hover:bg-gray-700 cursor-pointer"
                                          >
                                            <input
                                              type="checkbox"
                                              checked={isSelected}
                                              onChange={() => toggleFilterValue(column.key, value)}
                                              className="rounded border-gray-300 dark:border-gray-600 text-blue-600 focus:ring-blue-500"
                                            />
                                            <span className="text-sm text-label flex-1 truncate">
                                              {displayValue}
                                            </span>
                                          </label>
                                        );
                                      })}
                                  </div>
                                </div>
                              </div>
                            )}
                          </>
                        );
                      })()
                    ) : column.key === 'actions' ? (
                      // No filter for actions column
                      null
                    ) : (
                      <input
                        type="text"
                        value={filterValue}
                        onChange={(e) => handleFilterRowChange(column.key, e.target.value)}
                        placeholder={`Search ${column.label}...`}
                        className="w-full px-2 py-1 text-xs border border-gray-300 dark:border-gray-500 rounded bg-white dark:bg-gray-700 text-heading placeholder-gray-400 dark:placeholder-gray-500 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                    )}
                  </th>
                );
              })}
              {toolsColumn && (
                <th className="px-6 py-2 sticky right-0 bg-gray-100 dark:bg-gray-600 z-40 shadow-[-2px_0_5px_-2px_rgba(0,0,0,0.1)]"></th>
              )}
            </tr>
          </thead>
          <tbody className="bg-panel divide-y divide-gray-200 dark:divide-gray-700">
            {loading ? (
              <tr>
                <td colSpan={visibleColumnsArray.length + (toolsColumn ? 1 : 0)} className="px-6 py-12 text-center">
                  <div className="flex flex-col items-center justify-center space-y-3">
                    <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600 dark:border-blue-400"></div>
                    <p className="text-sm text-muted">Loading data...</p>
                  </div>
                </td>
              </tr>
            ) : paginatedData.length === 0 ? (
              <tr>
                <td colSpan={visibleColumnsArray.length + (toolsColumn ? 1 : 0)} className="px-6 py-8 text-center text-muted">
                  {errorMessage ? (
                    <span className="text-red-600 dark:text-red-400">{errorMessage}</span>
                  ) : (
                    'No data found'
                  )}
                </td>
              </tr>
            ) : (
              paginatedData.map((row, rowIndex) => {
                const rowClassName = getRowClassName ? getRowClassName(row) : '';

                return (
                  <tr
                    key={getRowKey(row)}
                    className={`${rowClassName} hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors`}
                    onDoubleClick={() => onRowDoubleClick?.(row)}
                  >
                    {visibleColumnsArray.map((column, colIndex) => {
                      // Only apply responsive classes if showFieldsToggle is disabled
                      // When showFieldsToggle is enabled, user controls visibility manually
                      const responsiveClass = !showFieldsToggle && column.responsive
                        ? column.responsive === 'always'
                          ? ''
                          : `hidden ${column.responsive}:table-cell`
                        : '';

                      const rowKey = getRowKey(row);
                      const isEditing = enableInlineEdit && editingCell?.rowKey === rowKey && editingCell?.columnKey === column.key;
                      const isSelected = enableInlineEdit && selectedCell?.rowIndex === rowIndex && selectedCell?.colIndex === colIndex;
                      const isSaving = enableInlineEdit && savingCell?.rowKey === rowKey && savingCell?.columnKey === column.key;
                      const cellValue = (row as any)[column.key];

                      return (
                        <td
                          key={column.key}
                          className={`px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100 ${responsiveClass} ${column.className || ''} ${
                            isSaving ? 'bg-green-100 dark:bg-green-900/30 transition-colors duration-300' :
                            isSelected ? 'ring-2 ring-blue-500 ring-inset bg-blue-50 dark:bg-blue-900/20' :
                            (column.sticky || column.stickyRight) ? 'bg-panel' : 'bg-panel'
                          } ${enableInlineEdit && column.editable !== false ? 'cursor-cell' : ''} ${
                            column.stickyRight
                              ? 'sticky right-0 z-10 shadow-[-2px_0_5px_-2px_rgba(0,0,0,0.1)]'
                              : column.sticky
                                ? 'sticky left-0 z-10 shadow-[2px_0_5px_-2px_rgba(0,0,0,0.1)]'
                                : ''
                          }`}
                          onClick={() => handleCellClick(rowIndex, colIndex)}
                          onDoubleClick={() => handleCellDoubleClick(rowIndex, colIndex)}
                        >
                          {isEditing ? (
                            // Editing mode
                            column.type === 'select' && column.options ? (
                              <select
                                ref={editInputRef as any}
                                value={editValue}
                                onChange={(e) => setEditValue(e.target.value)}
                                onKeyDown={handleEditKeyDown}
                                onBlur={commitEdit}
                                className="w-full px-2 py-1 bg-white dark:bg-gray-700 text-heading border border-blue-500 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                              >
                                <option value="">Select...</option>
                                {column.options.map((option, i) => {
                                  // Handle both string[] and SelectOption[] formats
                                  const optionValue = typeof option === 'string' ? option : option.value;
                                  const optionLabel = typeof option === 'string' ? option : option.label;
                                  return (
                                    <option key={i} value={optionValue}>
                                      {optionLabel}
                                    </option>
                                  );
                                })}
                              </select>
                            ) : (
                              <input
                                ref={editInputRef as any}
                                type={column.type === 'number' ? 'number' : column.type === 'date' ? 'date' : 'text'}
                                value={editValue}
                                onChange={(e) => setEditValue(e.target.value)}
                                onKeyDown={handleEditKeyDown}
                                onBlur={commitEdit}
                                className="w-full px-2 py-1 bg-white dark:bg-gray-700 text-heading border border-blue-500 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                              />
                            )
                          ) : (
                            // Display mode
                            column.render ? column.render(row) : (() => {
                              // For select columns with value/label options, display the label
                              if (column.type === 'select' && column.options && cellValue) {
                                const firstOption = column.options[0];
                                // Check if options are SelectOption objects or simple strings
                                if (typeof firstOption === 'object' && 'value' in firstOption) {
                                  // Options are SelectOption[]
                                  // Convert both to strings for comparison to handle type mismatches
                                  const option = (column.options as SelectOption[]).find(opt => String(opt.value) === String(cellValue));
                                  if (option) return option.label;
                                } else {
                                  // Options are string[]
                                  const option = (column.options as string[]).find(opt => String(opt) === String(cellValue));
                                  if (option) return option;
                                }
                              }
                              return cellValue || '-';
                            })()
                          )}
                        </td>
                      );
                    })}
                    {/* Tools Column Cell */}
                    {toolsColumn && (
                      <td className="px-6 py-4 whitespace-nowrap text-sm sticky right-0 bg-panel hover:bg-highlight transition-colors z-30 shadow-[-2px_0_5px_-2px_rgba(0,0,0,0.1)]">
                        <div className="flex items-center gap-3">
                          {toolsConfig?.showView && (
                            <button
                              onClick={() => toolsConfig.onView?.(row)}
                              className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                              title="View"
                            >
                              <MagnifyingGlassIcon className="w-5 h-5" />
                            </button>
                          )}
                          {toolsConfig?.showEdit && (
                            <button
                              onClick={() => toolsConfig.onEdit?.(row)}
                              className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                              title="Edit"
                            >
                              <PenSquareIcon className="w-5 h-5" />
                            </button>
                          )}
                          {toolsConfig?.customActions?.map((action, index) => {
                            const buttonClassName = typeof action.className === 'function'
                              ? action.className(row)
                              : action.className || "text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors";

                            return (
                              <button
                                key={index}
                                onClick={() => action.onClick(row)}
                                className={buttonClassName}
                                title={action.title}
                              >
                                {action.icon}
                              </button>
                            );
                          })}
                        </div>
                      </td>
                    )}
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination moved to footer - see SyncStatusFooter */}
    </div>
  );

  // Always wrap with PermissionConfigurable (using auto-generated or provided componentId)
  return (
    <PermissionConfigurable componentId={finalComponentId} componentName={title} className="block">
      {tableContent}
    </PermissionConfigurable>
  );
}

export default DataTable;
